import { SerializedConstructor } from '@langchain/core/load/serializable';

import { parseLangSmithParamsFromRun } from '@/utils/langsmith-params';

import {
  ManifestEditorProps,
  ManifestHubProps,
  ManifestPreviewProps,
} from '../Manifest.utils';
import { ManifestLLMChain } from './chains/ManifestLLMChain';
import {
  ManifestAzureChatOpenAI,
  ManifestAzureChatOpenAIPreview,
} from './chat_models/ManifestAzureChatOpenAI';
import {
  inferAzureChatOpenAIManifestFromRun,
  useManifestAzureChatOpenAIModelAndProvider,
} from './chat_models/ManifestAzureChatOpenAI.utils';
import {
  ManifestBedrockChat,
  ManifestBedrockChatPreview,
} from './chat_models/ManifestBedrockChat';
import {
  inferBedrockChatManifestFromRun,
  useBedrockChatModelAndProvider,
} from './chat_models/ManifestBedrockChat.utils';
import {
  ManifestChatAnthropic,
  ManifestChatAnthropicPreview,
} from './chat_models/ManifestChatAnthropic';
import {
  inferChatAnthropicManifestFromRun,
  useChatAnthropicModelAndProvider,
} from './chat_models/ManifestChatAnthropic.utils';
import { inferChatBedrockConverseManifestFromRun } from './chat_models/ManifestChatBedrockConverse.utils';
import {
  ManifestChatDeepSeek,
  ManifestChatDeepSeekPreview,
} from './chat_models/ManifestChatDeepSeek';
import {
  inferChatDeepSeekManifestFromRun,
  useChatDeepSeekModelAndProvider,
} from './chat_models/ManifestChatDeepSeek.utils';
import {
  ManifestChatFireworks,
  ManifestChatFireworksPreview,
} from './chat_models/ManifestChatFireworks';
import {
  inferChatFireworksManifestFromRun,
  useChatFireworksModelAndProvider,
} from './chat_models/ManifestChatFireworks.utils';
import {
  ManifestChatGoogleGenerativeAI,
  ManifestChatGoogleGenerativeAIPreview,
} from './chat_models/ManifestChatGoogleGenerativeAI';
import {
  inferChatGoogleGenerativeAIManifestFromRun,
  useChatGoogleGenerativeAIModelAndProvider,
} from './chat_models/ManifestChatGoogleGenerativeAI.utils';
import {
  ManifestChatGoogleVertexAI,
  ManifestChatGoogleVertexAIPreview,
} from './chat_models/ManifestChatGoogleVertexAI';
import {
  inferChatGoogleVertexAIManifestFromRun,
  useChatGoogleVertexAIModelAndProvider,
} from './chat_models/ManifestChatGoogleVertexAI.utils';
import {
  ManifestChatGroq,
  ManifestChatGroqPreview,
} from './chat_models/ManifestChatGroq';
import { inferChatGroqManifestFromRun } from './chat_models/ManifestChatGroq.utils';
import {
  ManifestChatMistralAI,
  ManifestChatMistralAIPreview,
} from './chat_models/ManifestChatMistralAI';
import {
  inferChatMistralAIManifestFromRun,
  useChatMistralAIModelAndProvider,
} from './chat_models/ManifestChatMistralAI.utils';
import {
  ManifestChatOpenAI,
  ManifestChatOpenAIPreview,
} from './chat_models/ManifestChatOpenAI';
import {
  inferChatOpenAIManifestFromRun,
  useChatOpenAIModelAndProvider,
} from './chat_models/ManifestChatOpenAI.utils';
import {
  ManifestChatXAI,
  ManifestChatXAIPreview,
} from './chat_models/ManifestChatXAI';
import {
  inferChatXAIManifestFromRun,
  useChatXAIModelAndProvider,
} from './chat_models/ManifestChatXAI.utils';
import { ManifestCustomModelEndpoint } from './chat_models/ManifestCustomModelEndpoint';
import { ManifestCustomModelEndpointPreview } from './chat_models/ManifestCustomModelEndpoint';
import {
  inferCustomModelEndpointManifestFromRun,
  useCustomModelEndpointModelAndProvider,
} from './chat_models/ManifestCustomModelEndpoint.utils';
import {
  ManifestAzureOpenAI,
  ManifestAzureOpenAIPreview,
} from './llms/ManifestAzureOpenAI';
import {
  inferAzureOpenAIManifestFromRun,
  useAzureOpenAIModelAndProvider,
} from './llms/ManifestAzureOpenAI.utils';
import {
  ManifestBedrock,
  ManifestBedrockPreview,
} from './llms/ManifestBedrock';
import { useBedrockModelAndProvider } from './llms/ManifestBedrock.utils';
import {
  ManifestFireworks,
  ManifestFireworksPreview,
} from './llms/ManifestFireworks';
import { useFireworksModelAndProvider } from './llms/ManifestFireworks.utils';
import {
  ManifestGoogleVertexAI,
  ManifestGoogleVertexAIPreview,
} from './llms/ManifestGoogleVertexAI';
import { useGoogleVertexAIModelAndProvider } from './llms/ManifestGoogleVertexAI.utils';
import { ManifestOpenAI, ManifestOpenAIPreview } from './llms/ManifestOpenAI';
import {
  inferOpenAIManifestFromRun,
  useOpenAIModelAndProvider,
} from './llms/ManifestOpenAI.utils';
import {
  ManifestChatPromptTemplate,
  ManifestChatPromptTemplateHub,
} from './prompts/ManifestChatPromptTemplate';
import {
  ManifestPromptTemplate,
  ManifestPromptTemplateHub,
} from './prompts/ManifestPromptTemplate';
import {
  ManifestStructuredPrompt,
  ManifestStructuredPromptHub,
} from './prompts/ManifestStructuredPrompt';
import {
  ManifestChatCustomModelPreview,
  ManifestCustomModel,
  ManifestCustomModelPreview,
} from './schema/runnable/ManifestCustomModel';
import {
  ManifestRunnableBinding,
  ManifestRunnableBindingPreview,
} from './schema/runnable/ManifestRunnableBinding';
import { useRunnableBindingModelAndProvider } from './schema/runnable/ManifestRunnableBinding.utils';

// This ensures function bivariance and correct assignability checks
// in function callbacks, at the expense of worse type safety for generic
// serialized constructor (now only `any`)
// TODO: consider upstreaming this to LangchainJS?
interface AnySerializedConstructor extends SerializedConstructor {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  kwargs: any;
}

type ManifestMap = Record<
  string,
  {
    preview?: React.FC<ManifestPreviewProps<AnySerializedConstructor>> | null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    editor?: React.FC<ManifestEditorProps<any>> | null;
    hub?: React.FC<ManifestHubProps<AnySerializedConstructor>> | null;
    getModelAndProvider?: (
      props: ManifestPreviewProps<AnySerializedConstructor>
    ) => { model: string | null; provider: string | null } | null;
    inferManifestFromRun?:
      | ((
          params: ReturnType<typeof parseLangSmithParamsFromRun> | null,
          serializedModel: SerializedConstructor
        ) => AnySerializedConstructor | null)
      | ((
          params: ReturnType<typeof parseLangSmithParamsFromRun> | null
        ) => AnySerializedConstructor | null);
  }
>;

export const base: ManifestMap = {
  'langchain/chat_models/openai/ChatOpenAI': {
    editor: ManifestChatOpenAI,
    preview: ManifestChatOpenAIPreview,
    // table: ManifestChatOpenAITable,
    getModelAndProvider: useChatOpenAIModelAndProvider,
    inferManifestFromRun: inferChatOpenAIManifestFromRun,
  },
  'langsmith/playground/CustomModelEndpoint': {
    editor: ManifestCustomModelEndpoint,
    preview: ManifestCustomModelEndpointPreview,
    getModelAndProvider: useCustomModelEndpointModelAndProvider,
    inferManifestFromRun: inferCustomModelEndpointManifestFromRun,
  },
  'langchain/chat_models/anthropic/ChatAnthropic': {
    editor: ManifestChatAnthropic,
    preview: ManifestChatAnthropicPreview,
    getModelAndProvider: useChatAnthropicModelAndProvider,
    inferManifestFromRun: inferChatAnthropicManifestFromRun,
  },
  'langchain/prompts/prompt/PromptTemplate': {
    editor: ManifestPromptTemplate,
    hub: ManifestPromptTemplateHub,
  },
  'langchain_core/prompts/prompt/PromptTemplate': {
    editor: ManifestPromptTemplate,
    hub: ManifestPromptTemplateHub,
  },
  'langchain/prompts/chat/ChatPromptTemplate': {
    editor: ManifestChatPromptTemplate,
    hub: ManifestChatPromptTemplateHub,
  },
  'langchain_core/prompts/chat/ChatPromptTemplate': {
    editor: ManifestChatPromptTemplate,
    hub: ManifestChatPromptTemplateHub,
  },
  'langchain_core/prompts/structured/StructuredPrompt': {
    editor: ManifestStructuredPrompt,
    hub: ManifestStructuredPromptHub,
  },
  'langchain/chains/llm/LLMChain': {
    editor: ManifestLLMChain,
  },
  'langchain/llms/openai/OpenAI': {
    editor: ManifestOpenAI,
    preview: ManifestOpenAIPreview,
    getModelAndProvider: useOpenAIModelAndProvider,
    inferManifestFromRun: inferOpenAIManifestFromRun,
  },
  'langchain/llms/openai/AzureOpenAI': {
    editor: ManifestAzureOpenAI,
    preview: ManifestAzureOpenAIPreview,
    getModelAndProvider: useAzureOpenAIModelAndProvider,
    inferManifestFromRun: inferAzureOpenAIManifestFromRun,
  },
  'langchain/llms/googlellm/VertexAI': {
    editor: ManifestGoogleVertexAI,
    preview: ManifestGoogleVertexAIPreview,
    getModelAndProvider: useGoogleVertexAIModelAndProvider,
  },
  'langchain/schema/runnable/RunnableBinding': {
    editor: ManifestRunnableBinding,
    preview: ManifestRunnableBindingPreview,
    getModelAndProvider: useRunnableBindingModelAndProvider,
  },
  'langchain/chat_models/google_genai/ChatGoogleGenerativeAI': {
    editor: ManifestChatGoogleGenerativeAI,
    preview: ManifestChatGoogleGenerativeAIPreview,
    getModelAndProvider: useChatGoogleGenerativeAIModelAndProvider,
    inferManifestFromRun: inferChatGoogleGenerativeAIManifestFromRun,
  },
  'langchain/chat_models/chat_integration/ProxyChatGoogle': {
    editor: ManifestChatGoogleGenerativeAI,
    preview: ManifestChatGoogleGenerativeAIPreview,
    getModelAndProvider: useChatGoogleGenerativeAIModelAndProvider,
    inferManifestFromRun: inferChatGoogleGenerativeAIManifestFromRun,
  },
  'langchain/chat_models/mistral_ai/ChatMistralAI': {
    editor: ManifestChatMistralAI,
    preview: ManifestChatMistralAIPreview,
    getModelAndProvider: useChatMistralAIModelAndProvider,
    inferManifestFromRun: inferChatMistralAIManifestFromRun,
  },
  'langchain/chat_models/chat_integration/ChatVertexAI': {
    editor: ManifestChatGoogleVertexAI,
    preview: ManifestChatGoogleVertexAIPreview,
    getModelAndProvider: useChatGoogleVertexAIModelAndProvider,
    inferManifestFromRun: inferChatGoogleVertexAIManifestFromRun,
  },
  'langchain/chat_models/groq/ChatGroq': {
    editor: ManifestChatGroq,
    preview: ManifestChatGroqPreview,
    inferManifestFromRun: inferChatGroqManifestFromRun,
  },
  'langchain/llms/fireworks/Fireworks': {
    editor: ManifestFireworks,
    preview: ManifestFireworksPreview,
    getModelAndProvider: useFireworksModelAndProvider,
  },
  'langchain/chat_models/fireworks/ChatFireworks': {
    editor: ManifestChatFireworks,
    preview: ManifestChatFireworksPreview,
    getModelAndProvider: useChatFireworksModelAndProvider,
    inferManifestFromRun: inferChatFireworksManifestFromRun,
  },
  'langchain/chat_models/azure_openai/AzureChatOpenAI': {
    editor: ManifestAzureChatOpenAI,
    preview: ManifestAzureChatOpenAIPreview,
    getModelAndProvider: useManifestAzureChatOpenAIModelAndProvider,
    inferManifestFromRun: inferAzureChatOpenAIManifestFromRun,
  },
  'langchain/llms/bedrock/Bedrock': {
    editor: ManifestBedrock,
    preview: ManifestBedrockPreview,
    getModelAndProvider: useBedrockModelAndProvider,
  },
  'langchain/chat_models/bedrock/BedrockChat': {
    editor: ManifestBedrockChat,
    preview: ManifestBedrockChatPreview,
    getModelAndProvider: useBedrockChatModelAndProvider,
    inferManifestFromRun: inferBedrockChatManifestFromRun,
  },
  'langsmith/playground/CustomModel': {
    editor: ManifestCustomModel,
    preview: ManifestCustomModelPreview,
  },
  'langsmith/playground/ChatCustomModel': {
    editor: ManifestCustomModel,
    preview: ManifestChatCustomModelPreview,
  },
  'langchain/chat_models/vertexai/ChatVertexAI': {
    editor: ManifestChatGoogleVertexAI,
    preview: ManifestChatGoogleVertexAIPreview,
    getModelAndProvider: useChatGoogleVertexAIModelAndProvider,
    inferManifestFromRun: inferChatGoogleVertexAIManifestFromRun,
  },
  'langchain/chat_models/mistralai/ChatMistralAI': {
    editor: ManifestChatMistralAI,
    preview: ManifestChatMistralAIPreview,
    getModelAndProvider: useChatMistralAIModelAndProvider,
    inferManifestFromRun: inferChatMistralAIManifestFromRun,
  },
  'langchain_google_genai/chat_models/ChatGoogleGenerativeAI': {
    editor: ManifestChatGoogleGenerativeAI,
    preview: ManifestChatGoogleGenerativeAIPreview,
    getModelAndProvider: useChatGoogleGenerativeAIModelAndProvider,
    inferManifestFromRun: inferChatGoogleGenerativeAIManifestFromRun,
  },
  'langchain/chat_models/bedrock/ChatBedrock': {
    editor: ManifestBedrockChat,
    preview: ManifestBedrockChatPreview,
    getModelAndProvider: useBedrockChatModelAndProvider,
    inferManifestFromRun: inferBedrockChatManifestFromRun,
  },
  'langchain_aws/chat_models/ChatBedrockConverse': {
    editor: ManifestBedrockChat,
    preview: ManifestBedrockChatPreview,
    getModelAndProvider: useBedrockChatModelAndProvider,
    inferManifestFromRun: inferChatBedrockConverseManifestFromRun,
  },
  'langchain_groq/chat_models/ChatGroq': {
    editor: ManifestChatGroq,
    preview: ManifestChatGroqPreview,
    inferManifestFromRun: inferChatGroqManifestFromRun,
  },
  'langchain_xai/chat_models/ChatXAI': {
    editor: ManifestChatXAI,
    preview: ManifestChatXAIPreview,
    getModelAndProvider: useChatXAIModelAndProvider,
    inferManifestFromRun: inferChatXAIManifestFromRun,
  },
  'langchain_deepseek/chat_models/ChatDeepSeek': {
    editor: ManifestChatDeepSeek,
    preview: ManifestChatDeepSeekPreview,
    getModelAndProvider: useChatDeepSeekModelAndProvider,
    inferManifestFromRun: inferChatDeepSeekManifestFromRun,
  },
};

export const useManifestMap = () => {
  return base;
};
